/**
 * Multi-package request router.
 *
 * A {@link Workspace} (and the {@link ArchlangServices} around it) is a single
 * resolution unit — one package. A real tree, and the website demo's worker
 * VFS, can hold MANY packages at once. Loading them all into one Workspace
 * resolves them as one flat package, so same-named top-level modules across
 * packages collide with a bogus `DUPLICATE_MODULE` (each package's root scope
 * is independent; a module name is unique only within its own package).
 *
 * The router restores isolation: it discovers every package root (via
 * {@link discoverRoots}), gives each its own single-package services, and
 * routes every per-URI request to the services that owns that file (via
 * {@link ownerOf}). Single-package trees collapse to one services — identical
 * to the pre-router behavior.
 *
 * Stdlib (`/__stdlib__`) is excluded from discovery and the workspace file
 * walk: it is a set of DEPENDENCY packages, auto-loaded per package by the
 * package-loader, never workspace files.
 */

import { ArchlangServices } from "../services.js";
import {
  discoverRoots,
  ownerOf,
  type DiscoveredRoot,
} from "./discover-roots.js";
import { loadPackage } from "./package-loader.js";
import {
  buildStdlibExportIndex,
  type StdlibExport,
} from "./stdlib-export-index.js";
import { matchesAny, type ArchlangConfig } from "./config.js";
import { isMarkdownPath } from "@archlang/viewer-core";
import type { LspIO } from "../io.js";

export interface RouterLogger {
  warn(msg: string): void;
}

const PRUNE = new Set(["node_modules", ".git", "dist", "__stdlib__"]);

function stripTrailingSlash(uri: string): string {
  return uri.endsWith("/") ? uri.slice(0, -1) : uri;
}

function isWorkspaceFile(uri: string): boolean {
  return uri.endsWith(".arch") || uri.endsWith(".archspace") || isMarkdownPath(uri);
}

export class PackageRouter {
  private readonly byRoot = new Map<string, ArchlangServices>();
  private packages_: DiscoveredRoot[] = [];
  private config: ArchlangConfig | undefined;
  /** Stdlib export index for auto-import (P3) — built once per bootstrap
   *  (parse-only, cached) and shared across every package's services. */
  private stdlibExports: readonly StdlibExport[] = [];

  constructor(
    private readonly io: LspIO,
    readonly folderUri: string,
    private readonly logger: RouterLogger,
  ) {}

  /**
   * Discover every package root under the folder and load each into its own
   * single-package services (files it owns + its resolved dependencies).
   */
  async bootstrap(config?: ArchlangConfig): Promise<void> {
    this.config = config;
    // Build the stdlib export index once (parse-only, cached) before loading
    // packages, so every services created below can auto-import stdlib kinds.
    this.stdlibExports = await this.buildStdlibIndex();
    const { packages } = await discoverRoots(this.io, this.folderUri);
    // discoverRoots yields an implicit root when loose files exist; if it
    // yields nothing at all (empty tree), keep a single implicit root so
    // routing always resolves to a services.
    this.packages_ =
      packages.length > 0
        ? packages
        : [
            {
              uri: stripTrailingSlash(this.folderUri),
              path: "",
              kind: "package",
              implicit: true,
            },
          ];

    // Partition every workspace file (stdlib excluded) by its owner package,
    // then load each package's owned files + dependencies into its services.
    const files = await this.walkFiles();
    const byOwner = new Map<string, string[]>();
    for (const uri of files) {
      const key = this.ownerKey(uri);
      const list = byOwner.get(key);
      if (list) list.push(uri);
      else byOwner.set(key, [uri]);
    }

    for (const pkg of this.packages_) {
      const svc = this.getOrCreate(pkg.path);
      // ONE batch seed per package — a per-file `setFile` runs a full
      // model rebuild per markdown/manifest entry (quadratic in documents;
      // minutes on a docs-heavy monorepo root).
      const entries: { uri: string; text: string }[] = [];
      for (const uri of byOwner.get(pkg.path) ?? []) {
        const text = await this.io.readFile(uri);
        if (text != null) entries.push({ uri, text });
      }
      svc.setFiles(entries);
      await this.loadDeps(pkg, svc);
    }
  }

  /** The services that owns a file URI (its enclosing package). */
  servicesForUri(uri: string): ArchlangServices {
    return this.getOrCreate(this.ownerKey(uri));
  }

  /** Re-group every services' diagnostics through a new config. */
  setConfig(config: ArchlangConfig): void {
    this.config = config;
    for (const svc of this.byRoot.values()) svc.workspace.setConfig(config);
  }

  setFile(uri: string, text: string, version = 0): void {
    this.servicesForUri(uri).setFile(uri, text, version);
  }

  removeFile(uri: string): void {
    this.servicesForUri(uri).removeFile(uri);
  }

  /** Every live package services (one per discovered package). */
  services(): ArchlangServices[] {
    return [...this.byRoot.values()];
  }

  /**
   * Every discovered package with the services holding its model. A caller
   * answering a per-package question (the nav tree an IDE renders) needs the
   * root's identity, which `services()` alone drops.
   */
  packages(): Array<{
    readonly root: DiscoveredRoot;
    readonly services: ArchlangServices;
  }> {
    return this.packages_.map((root) => ({
      root,
      services: this.getOrCreate(root.path),
    }));
  }

  // --- internals ---------------------------------------------------------

  private ownerKey(uri: string): string {
    const owner = ownerOf(this.relPath(uri), this.packages_);
    return owner ? owner.path : (this.packages_[0]?.path ?? "");
  }

  private getOrCreate(path: string): ArchlangServices {
    let svc = this.byRoot.get(path);
    if (!svc) {
      svc = new ArchlangServices();
      if (this.config) svc.workspace.setConfig(this.config);
      svc.setStdlibExports(this.stdlibExports);
      this.byRoot.set(path, svc);
    }
    return svc;
  }

  /** Build the stdlib export index (auto-import source, P3). Degrades to an
   *  empty index when no stdlib resolves — completion simply offers no
   *  auto-import items rather than throwing. */
  private async buildStdlibIndex(): Promise<readonly StdlibExport[]> {
    try {
      const stdlibUri = await this.io.defaultStdlibUri?.();
      if (!stdlibUri) return [];
      return await buildStdlibExportIndex(this.io, stdlibUri);
    } catch (err) {
      this.logger.warn(
        `archlang-lsp: failed to build stdlib export index: ${String(err)}`,
      );
      return [];
    }
  }

  /** The FOLDER-relative path `ownerOf` matches discovered package paths
   *  against. `memfs:///a/main.arch` under folder `memfs:///` → `a/main.arch`;
   *  `file:///Users/x/repo/a/main.arch` under folder `file:///Users/x/repo` →
   *  `a/main.arch`. Stripping only the scheme left the on-disk case holding the
   *  absolute prefix, so no nested package ever matched and every file fell to
   *  the implicit root — one merged model for the whole workspace. */
  private relPath(uri: string): string {
    const bare = (u: string): string => {
      const m = u.match(/^[a-z][a-z0-9+.-]*:\/+(.*)$/i);
      const body = m ? m[1]! : u;
      return body.replace(/^\/+/, "");
    };
    const base = bare(stripTrailingSlash(this.folderUri));
    const rel = bare(uri);
    if (base === "") return rel;
    if (rel === base) return "";
    return rel.startsWith(base + "/") ? rel.slice(base.length + 1) : rel;
  }

  private async loadDeps(
    pkg: DiscoveredRoot,
    svc: ArchlangServices,
  ): Promise<void> {
    try {
      const loaded = await loadPackage(this.io, pkg.uri);
      // One swap, one rebuild — clear + per-dep set is a rebuild per dep.
      const deps = new Map(
        [...loaded.dependencies].map(([name, dep]) => [name, dep.resolved]),
      );
      svc.workspace.replaceDependencies(deps);
    } catch (err) {
      this.logger.warn(
        `archlang-lsp: failed to load deps for ${pkg.uri}: ${String(err)}`,
      );
    }
  }

  private async walkFiles(): Promise<string[]> {
    const out: string[] = [];
    const base = stripTrailingSlash(this.folderUri);
    const stack = [base];
    const cfg = this.config;
    while (stack.length > 0) {
      const cur = stack.pop()!;
      let entries: readonly { name: string; kind: "file" | "dir" }[];
      try {
        entries = await this.io.readDirectory(cur);
      } catch {
        continue;
      }
      for (const e of entries) {
        if (PRUNE.has(e.name)) continue;
        const child = stripTrailingSlash(this.io.joinUri(cur, e.name));
        if (e.kind === "dir") {
          stack.push(child);
        } else if (isWorkspaceFile(child) && this.includedByConfig(child, base, cfg)) {
          out.push(child);
        }
      }
    }
    return out;
  }

  /** Apply the workspace `include`/`exclude` globs. The `include` glob targets
   *  `.arch` SOURCE files (default `**​/*.arch`); package manifests (`.archspace`)
   *  and markdown docs are STRUCTURAL — gated only by `exclude`, never `include`.
   *  Dropping a manifest would strip the package's `use … from arch.*` imports,
   *  so every typed declaration falls back to `module` with a spurious
   *  UNKNOWN_TYPE (the editor warnings the board never showed). */
  private includedByConfig(
    uri: string,
    base: string,
    cfg: ArchlangConfig | undefined,
  ): boolean {
    if (!cfg) return true;
    const rel = uri.startsWith(base + "/") ? uri.slice(base.length + 1) : uri;
    if (cfg.exclude.length && matchesAny(rel, cfg.exclude)) return false;
    const structural = isMarkdownPath(uri) || uri.endsWith(".archspace");
    if (!structural && cfg.include.length && !matchesAny(rel, cfg.include)) return false;
    return true;
  }
}
